// src/components/AdsPopup.jsx
import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../services/supabase';
import styles from './AdsPopup.module.css';

const AdsPopup = () => {
  const [allAds, setAllAds] = useState([]);
  const [currentAd, setCurrentAd] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const slideIntervalRef = useRef(null);
  const intervalRef = useRef(null);

  // Fetch all active ads from Supabase
  const fetchAds = async () => {
    const { data, error } = await supabase
      .from('ads')
      .select('*')
      .eq('is_active', true)
      .order('id', { ascending: true });
    if (!error && data && data.length > 0) {
      setAllAds(data);
      return data;
    }
    setAllAds([]);
    return [];
  };

  // Load ads on mount and listen to realtime changes
  useEffect(() => {
    let isMounted = true;

    const init = async () => {
      const ads = await fetchAds();
      if (!isMounted) return;
      if (ads.length === 0) {
        setCurrentAd(null);
        setShowPopup(false);
        return;
      }

      // Determine which ad to show
      const lastClose = localStorage.getItem('ad_last_close');
      const lastIndex = parseInt(localStorage.getItem('ad_next_index') || '0', 10) % ads.length;

      if (!lastClose) {
        // First time – show the first ad immediately
        const adToShow = ads[lastIndex % ads.length];
        setCurrentAd(adToShow);
        setShowPopup(true);
        // Advance index for next time
        const nextIndex = (lastIndex + 1) % ads.length;
        localStorage.setItem('ad_next_index', String(nextIndex));
      } else {
        const now = Date.now();
        const elapsed = now - parseInt(lastClose, 10);
        if (elapsed >= 10 * 60 * 1000) {
          // Cooldown passed – show the next ad
          const adToShow = ads[lastIndex % ads.length];
          setCurrentAd(adToShow);
          setShowPopup(true);
          // Advance index for next time
          const nextIndex = (lastIndex + 1) % ads.length;
          localStorage.setItem('ad_next_index', String(nextIndex));
          // Do NOT reset lastClose here – it will be reset when user closes
        } else {
          // Cooldown still active – keep popup hidden
          setCurrentAd(null);
          setShowPopup(false);
        }
      }
    };

    init();

    // Realtime subscription for ad changes
    const subscription = supabase
      .channel('ads-updates')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'ads' }, () => {
        if (isMounted) {
          fetchAds().then((ads) => {
            if (ads.length === 0) {
              setCurrentAd(null);
              setShowPopup(false);
            } else {
              // Do not force popup; let the cooldown logic handle it
              // We simply update the list, and the next cycle will pick up changes
              setAllAds(ads);
            }
          });
        }
      })
      .subscribe();

    return () => {
      isMounted = false;
      subscription.unsubscribe();
      clearInterval(intervalRef.current);
      clearInterval(slideIntervalRef.current);
    };
  }, []);

  // Periodic check to show the next ad after cooldown (runs every 10 seconds)
  useEffect(() => {
    if (allAds.length === 0) return;

    const checkCooldown = () => {
      const lastClose = localStorage.getItem('ad_last_close');
      if (!lastClose) return; // handled on mount

      const lastIndex = parseInt(localStorage.getItem('ad_next_index') || '0', 10) % allAds.length;
      const now = Date.now();
      const elapsed = now - parseInt(lastClose, 10);

      if (elapsed >= 10 * 60 * 1000 && !showPopup) {
        // Cooldown passed and no popup is currently shown
        const adToShow = allAds[lastIndex % allAds.length];
        setCurrentAd(adToShow);
        setShowPopup(true);
        // Advance index for next time
        const nextIndex = (lastIndex + 1) % allAds.length;
        localStorage.setItem('ad_next_index', String(nextIndex));
        // Do NOT reset lastClose – will be reset on close
      }
    };

    intervalRef.current = setInterval(checkCooldown, 10000); // every 10 seconds

    return () => clearInterval(intervalRef.current);
  }, [allAds, showPopup]);

  // Slideshow effect for multiple images in the current ad
  useEffect(() => {
    if (showPopup && currentAd && currentAd.images && currentAd.images.length > 1) {
      slideIntervalRef.current = setInterval(() => {
        setCurrentImageIndex((prev) => (prev + 1) % currentAd.images.length);
      }, 5000);
    } else {
      clearInterval(slideIntervalRef.current);
    }
    return () => clearInterval(slideIntervalRef.current);
  }, [showPopup, currentAd]);

  const handleClose = () => {
    setShowPopup(false);
    clearInterval(slideIntervalRef.current);
    setCurrentImageIndex(0);
    // Save the close timestamp to start the 10-minute cooldown
    localStorage.setItem('ad_last_close', String(Date.now()));
  };

  const handleImageClick = () => {
    if (currentAd?.url) window.open(currentAd.url, '_blank', 'noopener,noreferrer');
  };

  if (!showPopup || !currentAd || !currentAd.images || currentAd.images.length === 0) {
    return null;
  }

  return (
    <div className={styles.overlay}>
      <div className={styles.popupContainer}>
        <button onClick={handleClose} className={styles.closeButton}>✕</button>
        <div className={styles.imageWrapper} onClick={handleImageClick}>
          <img src={currentAd.images[currentImageIndex]} alt="Ad" className={styles.adImage} />
        </div>
        {currentAd.images.length > 1 && (
          <div className={styles.dotsContainer}>
            {currentAd.images.map((_, idx) => (
              <span
                key={idx}
                className={`${styles.dot} ${idx === currentImageIndex ? styles.activeDot : ''}`}
                onClick={() => setCurrentImageIndex(idx)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdsPopup;